/**
 * PAVING (2026-10-08, docs/bim-plan/26-paving-spike.md — step 1: path mode). Driveways and
 * walks drawn as a CENTERLINE path with a width, the way a civil alignment is drawn — never
 * as a traced outline. The surface is DERIVED from intent only:
 *   - a PROFILE along the path: each end's elevation (level + offset, a real-world site
 *     elevation, or PINNED to a picked floor / the terrain so it follows that element), plus
 *     optional grade breaks between; an even fall between them;
 *   - a CROSS-SLOPE across the width (% and which side drains low, or crowned);
 *   - CONTROL JOINTS at the type's maximum spacing, evenly divided between the ends and
 *     grade breaks (never a sliver panel). Each panel is one smooth surface — the 3D
 *     faceting falls on the joints, where real flatwork actually breaks (the spike's core
 *     idea), never on arbitrary triangles.
 * Every size is a firm standard on the TYPE (typical residential practice, Matt 2026-10-08
 * "use typical standards"): see seed.ts#ensurePavingLibrary for the values and their
 * sources. Geometry: bim-geometry categories/paving.
 */
import type { CategorySchema } from '../../registry'

const IN = 1 / 12

export const PAVING_EDGES = ['flush', 'thickened', 'curb'] as const
export type PavingEdge = (typeof PAVING_EDGES)[number]

const endGroup = (end: 'start' | 'end', label: string) => ({
  label,
  params: [
    {
      key: `${end}Mode`, label: 'Elevation', kind: 'choice' as const, default: 'offset',
      // ONE dropdown says how this end gets its height (Matt, 2026-10-09 - the separate
      // pin button hid it): a level offset, a typed SURVEY number, the terrain under the
      // end, or a picked slab (the garage). 'site' keeps its stored value for old docs;
      // only the label reads "Survey elevation" so it never sounds like the terrain.
      choices: [
        { value: 'offset', label: 'Level + offset' },
        { value: 'site', label: 'Survey elevation' },
        { value: 'terrain', label: 'On the terrain' },
        { value: 'slab', label: 'On a slab' },
      ],
    },
    {
      key: `${end}OffsetFt`, label: 'Offset from Level', kind: 'length' as const, default: 0, min: -50, max: 50,
      // a best-fit run takes its heights from the ground — only an "On a slab" end still counts
      conditionalOn: [{ param: `${end}Mode`, equals: 'offset' }, { param: 'surfaceFit', equals: 'ends' }],
    },
    {
      // stored project-relative like every length; shown in real-world feet through the
      // site datum (paramFields.jsx's ElevationInput) — the stair precedent
      key: `${end}SiteElevationFt`, label: 'Survey Elevation', kind: 'length' as const, default: 0,
      min: -500, max: 5000, siteElevation: true,
      conditionalOn: [{ param: `${end}Mode`, equals: 'site' }, { param: 'surfaceFit', equals: 'ends' }],
    },
    {
      // 'slab' mode's picked floor (the garage slab) - set by the Modify tab's "Pick start /
      // end slab...", never a form field. The end follows that slab live.
      key: `${end}RefId`, label: 'Slab', kind: 'data' as const, default: null, positional: true, hidden: true,
    },
  ],
})

export const pavingSchema: CategorySchema = {
  id: 'paving',
  pathParamKey: 'path',
  label: 'Paving',
  // under buildings, over the terrain (topo is 20)
  pickRank: 15,
  typeParams: {
    groups: [
      {
        label: 'Construction',
        params: [
          {
            // STREETS (2026-10-20, docs/bim-plan/35): a Street is a paving type drawn along OUR
            // curb's FLOWLINE (frontage only); its cross section, from the street out to the
            // back of walk, is the Street group below. 'run' = a driveway / walk down its centerline.
            key: 'pavingKind', label: 'Kind', kind: 'choice', default: 'run',
            choices: [
              { value: 'run', label: 'Driveway or walk' },
              { value: 'street', label: 'Street (drawn along the flowline)' },
            ],
          },
          { key: 'surfaceMaterial', label: 'Material', kind: 'material', default: '' },
          { key: 'thicknessFt', label: 'Thickness', kind: 'length', default: 4 * IN, min: 1 * IN, max: 2, conditionalOn: { param: 'pavingKind', equals: 'run' } },
          { key: 'widthFt', label: 'Width', kind: 'length', default: 4, min: 1, max: 60, conditionalOn: { param: 'pavingKind', equals: 'run' } },
          {
            // 0 = no joints (asphalt, a single pour drawn as one panel); on a street, the
            // sidewalk's score joints
            key: 'jointSpacingFt', label: 'Joint Spacing (max)', kind: 'length', default: 5, min: 0, max: 30,
          },
        ],
      },
      {
        // the street's cross section from the street out to the property — every value a
        // typical DEFAULT the firm edits (cities each have their own curb and gutter standard)
        label: 'Street',
        params: [
          {
            key: 'curbType', label: 'Curb', kind: 'choice', default: 'vertical', conditionalOn: { param: 'pavingKind', equals: 'street' },
            choices: [
              { value: 'vertical', label: 'Vertical curb' },
              { value: 'roll', label: 'Rolled curb' },
              { value: 'none', label: 'No curb' },
            ],
          },
          { key: 'streetCurbHeightFt', label: 'Curb Height', kind: 'length', default: 6 * IN, min: 1 * IN, max: 1.5, conditionalOn: [{ param: 'pavingKind', equals: 'street' }, { param: 'curbType', equals: ['vertical', 'roll'] }] },
          { key: 'streetCurbWidthFt', label: 'Curb Top Width', kind: 'length', default: 6 * IN, min: 2 * IN, max: 2, conditionalOn: [{ param: 'pavingKind', equals: 'street' }, { param: 'curbType', equals: ['vertical', 'roll'] }] },
          { key: 'rollWidthFt', label: 'Roll Width', kind: 'length', default: 12 * IN, min: 3 * IN, max: 3, conditionalOn: [{ param: 'pavingKind', equals: 'street' }, { param: 'curbType', equals: 'roll' }] },
          { key: 'curbDepthFt', label: 'Curb and Gutter Depth', kind: 'length', default: 8 * IN, min: 3 * IN, max: 2, conditionalOn: { param: 'pavingKind', equals: 'street' } },
          { key: 'gutter', label: 'Gutter', kind: 'bool', default: true, conditionalOn: { param: 'pavingKind', equals: 'street' } },
          { key: 'gutterWidthFt', label: 'Gutter Width', kind: 'length', default: 24 * IN, min: 6 * IN, max: 4, conditionalOn: [{ param: 'pavingKind', equals: 'street' }, { param: 'gutter', equals: true }] },
          { key: 'gutterSlopePct', label: 'Gutter Slope %', kind: 'number', default: 6.25, min: 0, max: 20, conditionalOn: [{ param: 'pavingKind', equals: 'street' }, { param: 'gutter', equals: true }] },
          { key: 'laneMaterial', label: 'Street Material', kind: 'material', default: '', conditionalOn: { param: 'pavingKind', equals: 'street' } },
          { key: 'laneWidthFt', label: 'Street Shown', kind: 'length', default: 10, min: 0, max: 40, conditionalOn: { param: 'pavingKind', equals: 'street' } },
          { key: 'laneThicknessFt', label: 'Street Paving Thickness', kind: 'length', default: 4 * IN, min: 1 * IN, max: 2, conditionalOn: { param: 'pavingKind', equals: 'street' } },
          { key: 'laneSlopePct', label: 'Street Cross Slope %', kind: 'number', default: 2, min: 0, max: 10, conditionalOn: { param: 'pavingKind', equals: 'street' } },
          { key: 'parkwayWidthFt', label: 'Parkway Width', kind: 'length', default: 0, min: 0, max: 30, conditionalOn: { param: 'pavingKind', equals: 'street' } },
          { key: 'walkWidthFt', label: 'Sidewalk Width', kind: 'length', default: 5, min: 0, max: 20, conditionalOn: { param: 'pavingKind', equals: 'street' } },
          { key: 'walkThicknessFt', label: 'Sidewalk Thickness', kind: 'length', default: 4 * IN, min: 1 * IN, max: 1, conditionalOn: { param: 'pavingKind', equals: 'street' } },
          { key: 'walkSlopePct', label: 'Parkway and Sidewalk Slope %', kind: 'number', default: 2, min: 0, max: 10, conditionalOn: { param: 'pavingKind', equals: 'street' } },
        ],
      },
      {
        // DRIVEWAY APPROACHES (2026-10-20, docs/bim-plan/35 step 2): where a driveway meets
        // this street the curb is cut, dropped to a lip, ramped back up over the wings, and an
        // apron climbs from the lip to the sidewalk — typical DEFAULTS the firm edits (cities
        // each have their own approach standard). A rolled curb or no curb is never cut.
        label: 'Driveway Approach',
        params: [
          { key: 'approachLipFt', label: 'Lip Height', kind: 'length', default: 1 * IN, min: 0, max: 6 * IN, conditionalOn: [{ param: 'pavingKind', equals: 'street' }, { param: 'curbType', equals: 'vertical' }] },
          { key: 'approachWingFt', label: 'Wing Length (each side)', kind: 'length', default: 3, min: 0, max: 10, conditionalOn: [{ param: 'pavingKind', equals: 'street' }, { param: 'curbType', equals: 'vertical' }] },
          { key: 'approachDepthFt', label: 'Apron Depth (from the flowline)', kind: 'length', default: 3, min: 1, max: 15, conditionalOn: [{ param: 'pavingKind', equals: 'street' }, { param: 'curbType', equals: 'vertical' }] },
          { key: 'approachThicknessFt', label: 'Apron Thickness', kind: 'length', default: 6 * IN, min: 3 * IN, max: 1, conditionalOn: [{ param: 'pavingKind', equals: 'street' }, { param: 'curbType', equals: 'vertical' }] },
        ],
      },
      {
        label: 'Drainage',
        params: [
          { key: 'crossSlopePct', label: 'Cross Slope %', kind: 'number', default: 1.5, min: 0, max: 10, conditionalOn: { param: 'pavingKind', equals: 'run' } },
          { key: 'maxSlopePct', label: 'Max Running Slope %', kind: 'number', default: 5, min: 0, max: 50 },
        ],
      },
      {
        label: 'Edges',
        params: [
          {
            key: 'edgeType', label: 'Edge', kind: 'choice', default: 'flush', conditionalOn: { param: 'pavingKind', equals: 'run' },
            choices: [
              { value: 'flush', label: 'Flush to grade' },
              { value: 'thickened', label: 'Thickened edge' },
              { value: 'curb', label: 'Curb' },
            ],
          },
          { key: 'thickenedWidthFt', label: 'Thickened Width', kind: 'length', default: 12 * IN, min: 2 * IN, max: 3, conditionalOn: [{ param: 'pavingKind', equals: 'run' }, { param: 'edgeType', equals: 'thickened' }] },
          { key: 'thickenedDepthFt', label: 'Thickened Extra Depth', kind: 'length', default: 4 * IN, min: 1 * IN, max: 2, conditionalOn: [{ param: 'pavingKind', equals: 'run' }, { param: 'edgeType', equals: 'thickened' }] },
          { key: 'curbHeightFt', label: 'Curb Height', kind: 'length', default: 6 * IN, min: 1 * IN, max: 2, conditionalOn: [{ param: 'pavingKind', equals: 'run' }, { param: 'edgeType', equals: 'curb' }] },
          { key: 'curbWidthFt', label: 'Curb Width', kind: 'length', default: 6 * IN, min: 2 * IN, max: 2, conditionalOn: [{ param: 'pavingKind', equals: 'run' }, { param: 'edgeType', equals: 'curb' }] },
        ],
      },
    ],
  },
  instanceParams: {
    groups: [
      {
        label: 'Constraints',
        params: [
          { key: 'path', label: 'Path', kind: 'path2d', default: null },
          {
            // BEST FIT (2026-10-12, Matt on Foulger: an existing driveway being resurfaced, not
            // regraded, should "find the best fit to that existing terrain"): 'ends' = the
            // designed surface (end heights + cross slope); 'terrain' = fitted to the existing
            // ground at every joint, both directions (an end "On a slab" still lands on it)
            key: 'surfaceFit', label: 'Surface', kind: 'choice', default: 'ends', conditionalOn: { param: 'pavingKind', equals: 'run' },
            choices: [
              { value: 'ends', label: 'From its end heights' },
              { value: 'terrain', label: 'Best fit to the existing terrain' },
            ],
          },
          // nullable overrides shadowing the type (the casework height pattern)
          { key: 'widthFt', label: 'Width', kind: 'length', default: null, optional: true, min: 1, max: 60, conditionalOn: { param: 'pavingKind', equals: 'run' } },
          { key: 'crossSlopePct', label: 'Cross Slope %', kind: 'number', default: null, optional: true, min: 0, max: 10, conditionalOn: { param: 'pavingKind', equals: 'run' } },
          {
            // which side of the drawn direction drains LOW (left/right of start → end)
            key: 'crossSlopeSide', label: 'Drains To', kind: 'choice', default: 'right', conditionalOn: { param: 'pavingKind', equals: 'run' },
            choices: [
              { value: 'right', label: 'Right side' },
              { value: 'left', label: 'Left side' },
              { value: 'crown', label: 'Both sides (crowned)' },
            ],
          },
          {
            // STREETS: which side of the drawn flowline (left / right of start -> end) the
            // property is on - set toward the house when drawn, flipped with "Flip side"
            key: 'propertySide', label: 'Property Side', kind: 'choice', default: 'left', conditionalOn: { param: 'pavingKind', equals: 'street' },
            choices: [{ value: 'left', label: 'Left of the line' }, { value: 'right', label: 'Right of the line' }],
          },
          {
            // GRADE BREAKS (on a street: the CURB SHOTS, flowline elevations typed from the
            // survey with "Add curb shot"): [{ stationFt, zFt }] — station along the drawn path from its
            // start, z project-relative feet. A joint always lands on a break.
            key: 'gradeBreaks', label: 'Grade Breaks', kind: 'data', default: null, positional: true, hidden: true,
          },
          {
            // custom joint stations (the spike's hand-drawn joints, step 3) — null = automatic
            key: 'joints', label: 'Joints', kind: 'data', default: null, positional: true, hidden: true,
          },
        ],
      },
      endGroup('start', 'Start (first point)'),
      endGroup('end', 'End (last point)'),
    ],
  },
}
